import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppBar, Dialog, DialogActions, DialogContent, Tab, Tabs, Button } from '@material-ui/core';
import './UserProfile.css';
import LoadingPanel from '@/component/common/LoadingPanel';
import PersonalSettingTab from './PersonalSettingTab';
import UserProfileTab from './UserProfileTab';
import LocalMCPIntegrationTab from './LocalMCPIntegrationTab';
import McpTokenTab from './McpTokenTab';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { closeTabId, mcpActivationChangedAtom } from '@/state/atom/panelAtom';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import Constant from '@/common/constant/Constant';
import { corporationAtom } from '@/state/atom/corporationAtom';
import Utils from '@/common/utils/utils';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import CorporationUtils from '@/common/utils/CorporationUtils';

export default function UserProfile(props) {
    // Re-render when the translation dictionary loads/extends so tr() tab labels update.
    useTranslationSubscription();

    const userDetail = useRecoilValue(userAtom);
    const corporationDetail = useRecoilValue(corporationAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const mcpActivationChanged= useRecoilValue(mcpActivationChangedAtom);

    const [isLoading, setIsLoading] = useState(false);
    const [tabSelected, setTabSelected] = useState(props?.initialTab || 'userProfile');
    const isMobileOrTablet = useMemo(() => Utils.isMobileOrTabletDevice(), []);
    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);
    const [submitChangeUserProfileStatus, setSubmitChangeUserProfileStatus] = useState(0);
    const [isProfileHasChange, setIsProfileHasChange] = useState(false);
    const [closeTabIdState, setCloseTabIdState] = useRecoilState(
        closeTabId
    );
    const [isClickCloseTab,setClickCloseTab] = useState(false);

    useEffect(() => {
        if (closeTabIdState === 'system_user_profile') {
            setClickCloseTab(true)
            if (isProfileHasChange) {
                setIsOpenDialog(true);
            } else {
                closePanelTab({
                    event: null,
                    panelType: Constant.PANEL_TYPE.PERSONAL_MANAGEMENT,
                    panelId: props.panelId,
                    tabId: 'system_user_profile'
                });
            }
            setCloseTabIdState(0);
        }
    }, [closeTabIdState]);

    const onChangeTab = (event, newValue) => {
        if (
            tabSelected === 'userProfile' &&
            newValue === 'persionalSetting' &&
            isProfileHasChange === true
        ) {
            setIsOpenDialog(true);
        } else {
            setTabSelected(newValue);
        }
    };

    const onClickBtnCloseDialog = (event, closePopup) => {
        setIsOpenDialog(false);
        if (isClickCloseTab) {
            closePanelTab({
                event: null,
                panelType: Constant.PANEL_TYPE.PERSONAL_MANAGEMENT,
                panelId: props.panelId,
                tabId: 'system_user_profile'
            });
        } else {
            if (closePopup !== true) {
                setTabSelected('persionalSetting');
                setIsProfileHasChange(false)
            }
        }
    };
    const confirmSubmitChangeUserProfile = status => {
        setSubmitChangeUserProfileStatus(status);
        setIsOpenDialog(false);
    };


    const isDisplayLocalMcpTab = useMemo(() => {
        if (!corporationDetail || !projectInuseDetail || !userDetail) return false;
        
        // check ON/OFF service
        const isEnableMCPService = CorporationUtils.isCanUsingMCPService(corporationDetail, projectInuseDetail);
        
        // check is some ON mcp in prj — prefer atom value (updated on toggle) over stale projectSetting
        const prjOfUserLocalMCP = {
            ...(Utils.convertJSONDataToObject(projectInuseDetail?.projectSetting)?.localMcpActivated || {}),
            ...(mcpActivationChanged?.userLocalMcp || {})
        };

        if (Utils.isEmptyObject(prjOfUserLocalMCP)) return false;

        const isEnableLocalMcpItem = Object.values(prjOfUserLocalMCP).some(v => v === true);
        if (!isEnableLocalMcpItem) return false;

        return isEnableMCPService;
    }, [corporationDetail?.id, projectInuseDetail?.id, userDetail?.id, mcpActivationChanged?.userLocalMcp]);

    const isDisplayMcpTokenTab = useMemo(() => {
        if (!corporationDetail || !projectInuseDetail?.id || !userDetail) return false;

        // Only General projects can have analytics enabled (Base/Guest/B2C/Partner cannot)
        if (projectInuseDetail?.type !== Constant.PROJECT_TYPE.GENERAL) return false;

        const limitValue = Utils.convertJSONDataToObject(corporationDetail?.limitValue);
        const corporationSetting = Utils.convertJSONDataToObject(corporationDetail?.corporationSetting);
        const projectSetting = Utils.convertJSONDataToObject(projectInuseDetail?.projectSetting);

        return (
            limitValue?.logAnalysis === Constant.STATUS_SERVICE_PERMISSION.ON &&
            corporationSetting?.usingKibanaAnalysis === Constant.STATUS_SERVICE_PERMISSION.ON &&
            projectSetting?.usingKibanaAnalysis === true
        );
    }, [corporationDetail, projectInuseDetail, userDetail]);

    // Tab is open but the condition is no longer met (Org OFF / Project has no Local MCP
    // enabled / mobile or tablet view) → fall back to the first tab.
    useEffect(() => {
        if (tabSelected === 'localMcpIntegration' && (!isDisplayLocalMcpTab || isMobileOrTablet)) {
            setTabSelected('userProfile');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDisplayLocalMcpTab, tabSelected, isMobileOrTablet]);

    const mcpTokenTabWasOpenRef = useRef(false);
    useEffect(() => {
        if (tabSelected === 'integrationToken' && isDisplayMcpTokenTab) {
            mcpTokenTabWasOpenRef.current = true;
        }
    }, [tabSelected, isDisplayMcpTokenTab]);

    useEffect(() => {
        if (tabSelected === 'integrationToken' && !isDisplayMcpTokenTab && !mcpTokenTabWasOpenRef.current) {
            setTabSelected('userProfile');
        }
    }, [isDisplayMcpTokenTab, tabSelected]);

    const renderContentTab = tabSelect => {
        switch (tabSelect) {
            case 'userProfile': {
                return (
                    <UserProfileTab
                        setIsLoading={setIsLoading}
                        api={props?.API}
                        submitChangeUserProfileStatus={submitChangeUserProfileStatus}
                        setSubmitChangeUserProfileStatus={setSubmitChangeUserProfileStatus}
                        setIsProfileHasChange={setIsProfileHasChange}
                        setTabSelected={setTabSelected}
                        {...props}
                    />
                );
            }
            case 'persionalSetting': {
                return <PersonalSettingTab userDetail={userDetail} API={props?.API} />;
            }
            case 'localMcpIntegration': {
                return (
                    <LocalMCPIntegrationTab isDisplayLocalMcpTab={isDisplayLocalMcpTab}/>
                );
            }
            case 'integrationToken': {
                return (
                    <McpTokenTab
                        organizationId={corporationDetail?.id}
                        projectId={projectInuseDetail?.id}
                        userId={userDetail?.id}
                    />
                );
            }
            default: {
                return <div>{tr('Nothing to show')}</div>;
            }
        }
    };

    return (
        <div className="userProfile">
            <LoadingPanel isLoading={isLoading} />
            {/* user detail */}
            <div className="tabList">
                <div className="navTabs">
                    <AppBar className="navTabs-box" position="static">
                        <Tabs
                            className="navTabs-rot"
                            value={tabSelected}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="scrollable auto tabs example"
                            onChange={onChangeTab}
                        >
                            <Tab
                                className="navTabs--item custom-tab"
                                label={tr('ユーザープロフィール')}
                                value="userProfile"
                            />
                            <Tab
                                className="navTabs--item custom-tab"
                                label={tr('通知設定')}
                                value="persionalSetting"
                            />
                            {/* comment personal connector */}
                            {/* {isDisplayExternalIntegrationTab() && <Tab
                                className="navTabs--item custom-tab"
                                label="外部連携設定"
                                value="externalIntegration"
                            />} */}
                            {/* MCP-003-F3 BR-11: the 外部連携設定 tab only shows when the Org has
                                外部連携 enabled and the current Project has at least one Local MCP
                                enabled (eligible flag from BE). */}
                            {isDisplayLocalMcpTab && !isMobileOrTablet && (
                                <Tab
                                    className="navTabs--item custom-tab"
                                    label={tr('外部連携設定')}
                                    value="localMcpIntegration"
                                />
                            )}
                            {(isDisplayMcpTokenTab || (tabSelected === 'integrationToken' && mcpTokenTabWasOpenRef.current)) && (
                                <Tab
                                    className="navTabs--item custom-tab"
                                    label={trI18n('MCP_TOKEN_TAB')}
                                    value="integrationToken"
                                />
                            )}
                        </Tabs>
                    </AppBar>
                </div>
                <Dialog
                open={isOpenDialog}
                onClose={(event, reason) => onClickBtnCloseDialog(event, true)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="wrapDialog"
                // disableBackdropClick="true"
            >
                {/* <DialogTitle className="titleHeader">
                        <h2 className="title">{trI18n('COMLB001')}</h2>
                    </DialogTitle> */}
                <DialogContent>
                    <div className="confirm-dialog-content">{trI18n('COMMSG004')}</div>
                </DialogContent>
                <DialogActions className="confirm-dialog-action">
                    {/* btn confirm */}
                    {isClickCloseTab ? <Button
                        className="btn-hg32 btn-handle-delete-user-agree"
                        variant="contained"
                        onClick={e => confirmSubmitChangeUserProfile(2)}
                    >
                        {trI18n('COMBTN002')}
                    </Button> :  <Button
                        className="btn-hg32 btn-handle-delete-user-agree"
                        variant="contained"
                        onClick={e => confirmSubmitChangeUserProfile(1)}
                    >
                        {trI18n('COMBTN002')}
                    </Button>}
                    {/* btn cancel */}
                    <Button
                        className="btn-light btn-hg32"
                        variant="contained"
                        onClick={onClickBtnCloseDialog}
                    >
                        {trI18n('COMBTN007')}
                    </Button>
                </DialogActions>
            </Dialog>
            </div>
            {renderContentTab(tabSelected)}
        </div>
    );
}
